import React, { useEffect, useRef } from "react";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css'; 
import { useNavigate } from "react-router";

export default function Recommendations({ recommendations, name }) {
    const navigate = useNavigate();

    useEffect(() => {
        if (recommendations.length > 0 && window.bvAPIURL) {
                const script = document.createElement('script');

                script.async = true;
                script.type = 'text/javascript';
                script.src = window.bvAPIURL;
                document.getElementsByTagName('head')[0].appendChild(script);                
        }
    }, []);

    const startOver = (e) => {
        e.preventDefault();
        navigate('/');
    };

    return (
        <div>
            <header className="recommendations-header">
                <h1>{name}&apos;s SmartPaks</h1>
                <div>RECOMMENDATIONS</div>
            </header>
            <div className="recommendations">
                {recommendations.map((category, idx) => (
                    <div key={idx} className="recommendation-category" >
                        <div className="recommendation-content">
                            <h2>{category.categoryName}</h2>
                            <p>{category.recommendationText}</p>
                            {category.additionalRecommendationText && (<p>{category.additionalRecommendationText}</p>)}
                        </div>
                        <ProductCards products={category.products}  />
                    </div>
                ))}
            </div>
            <div className="start-over-link">
                <a href="#" onClick={startOver}>Start Over</a>
            </div>
        </div>
    );
}

function ProductCards({products}){
    const gap = '2.4rem';

    if (products.length === 1) {
        return (
            <div className="recommendation-products">
                <ProductCard product={products[0]} />
            </div>
        );
    }

    return (
        <div className="recommendation-products">
            <Splide
                aria-label="Recommended Products"
                style={{
                    minWidth: `calc(${100 / products.length}% - ${gap} + ${gap} / ${products.length})`
                }}
                options={{
                    autoWidth: true,
                    gap: gap,
                    pagination: false
                }}
            >                
            {products.map(product => (
                <SplideSlide key={product.productClassId}>
                    <ProductCard product={product} />
                </SplideSlide>
            ))}
            </Splide>
        </div>
    );
}

function ProductCard({product}){
    return (
        <div key={product.productId} className="product-card">
            <a href={product.productUrl || '#'} rel="noopener noreferrer">
                <ProductImage src={product.imagePath} alt="" />
                <ReviewStars id={product.productClassId} />
                <div className="product-name" dangerouslySetInnerHTML={{ __html: product.productName }} />
                <PriceAlt price={product.price} />
                <div className="product-price-per-day">{product.pricePerDay}/day</div>
                <div className="product-cta">Shop Now</div>
            </a>
        </div>
    );
}

function PriceAlt({price}){
    const parts = price.split('.');

    return (
        <div className="product-price">
            <span>{parts[0]}</span><span>.{parts[1]}</span>
        </div>
    );
}

function ProductImage({ src, alt }) {
    return (
        <img width="192" height="192" loading="lazy" srcSet={`${src}?w=192 1x, ${src}?w=384 2x`} alt={alt || ''} className="product-image" />
    );
}

function ReviewStars({id}){
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current){
            whenElementsAreAddedToDOM(ref.current, $elements => {
                removeImportantOveriddenStyles($elements);
            });

            if (!hasReviewStars(ref.current)) {
                ref.current.style.display = 'none';
            }
        }
    }, []);

    return (<div ref={ref} className="product-rating" data-bv-show="inline_rating" data-bv-product-id={id}></div>);
}

function hasReviewStars($container){
    const $count = $container.querySelector('.bv_text');

    return $count && $count.textContent !== '(0)';
}

function removeImportantOveriddenStyles($elements){
    $elements
        .reduce((arr, nodes) => arr.concat([...nodes.querySelectorAll('svg')]), [])
        .forEach($svg => $svg.removeAttribute('style'));
}

function whenElementsAreAddedToDOM($element, onAdd){
    if (!$element || typeof onAdd !== 'function') {
        return;
    }

    const mutationObserver = new MutationObserver((mutationList) => {
        const ELEMENT_NODE = 1;

        if (!mutationList || !mutationList.length) {
            return;
        }

        for (const mutation of mutationList) {
            if (mutation.type === 'childList' && mutation.addedNodes.length) {
                onAdd([...mutation.addedNodes].filter(node => node.nodeType === ELEMENT_NODE));
            }
        }
    });

    mutationObserver.observe($element, { attributes: false, childList: true, subtree: true });
}